import { useCallback, useState } from "react";
import { Button } from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useCase, useCaseBlocks, useCaseSpecimens } from "../../hooks/useCases";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  getDisplayCaseNumber,
  getPhysicalSlideNumber,
} from "../../utils/caseIdentity";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import { requireDateOnlyString } from "../../utils/dateOnly";
import {
  printSelectedSlide,
  selectedPrintError,
} from "../../services/selectedMaterialPrint.service";
import type { CaseSlide } from "../../services/cases.service";

interface Props {
  caseId: string;
  slide: CaseSlide;
  disabled?: boolean;
}

export function PrintSlideButton({ caseId, slide, disabled }: Props) {
  const { data: caseData } = useCase(caseId);
  const { data: blocks } = useCaseBlocks(caseId);
  const { data: specimens } = useCaseSpecimens(caseId);
  const canViewCase = useHasPermission("cases.read");
  const [printing, setPrinting] = useState(false);
  const queryClient = useQueryClient();
  const handlePrint = useCallback(async () => {
    if (!caseData || !blocks || !specimens || printing) return;
    const specimen = specimens.find((item) => item.id === slide.specimenId);
    const block = blocks.find((item) => item.id === slide.blockId);
    if (!specimen || !block) return;
    setPrinting(true);
    try {
      const caseNumber = getDisplayCaseNumber(caseData);
      const slideNumber = getPhysicalSlideNumber(slide);
      await printSelectedSlide({
        caseId,
        slideId: slide.id,
        source: "case",
        label: {
          caseNumber,
          specimenLetter: specimen.number,
          blockNumber: String(block.number),
          slideNumber,
          patientName:
            resolvePatientLabelName(
              caseData.patient,
              caseData.draftPatientData
            ) ?? "",
          stainCode: slide.stain?.code || "H&E",
          barcode: `${caseNumber};${specimen.number};${block.number};${slideNumber}`,
          dateReceived: caseData.dateReceived
            ? requireDateOnlyString(caseData.dateReceived, "Date received")
            : undefined,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: ["case-slides", caseId],
      });
      notifications.show({
        title: "Slide Printed",
        message: "The selected slide label was printed.",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Print Failed",
        message: selectedPrintError(error),
        color: "red",
      });
    } finally {
      setPrinting(false);
    }
  }, [caseData, blocks, specimens, printing, slide, caseId, queryClient]);

  return (
    <Button
      size="compact-xs"
      variant="subtle"
      leftSection={<IconPrinter size={12} />}
      onClick={handlePrint}
      loading={printing}
      disabled={
        disabled ||
        !canViewCase ||
        !caseData ||
        !blocks ||
        !specimens ||
        caseData.isBlocked
      }
    >
      Print Slide
    </Button>
  );
}
